import styled from "@emotion/styled";
import { useEffect } from "react";

import { MDSDialogSlotName, MDSModalDialog } from "@/mds/components/overlays/dialog";
import { MDSLoadingSpinner } from "@/mds";
import type { IMessageReturnToMessagesTitle } from "@/modules/mem-agent/imessage-return-to-messages";

export const IMESSAGE_RETURN_TO_MESSAGES_REDIRECT_DELAY_MS = 500;

export interface IMessageReturnToMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRedirect: () => void;
  title: IMessageReturnToMessagesTitle;
}

/**
 * Gives iMessage-claim users a brief, cancellable handoff back to their Mem
 * Messages thread after they leave the post-claim upsell surface.
 */
export const IMessageReturnToMessagesModal = ({
  isOpen,
  onClose,
  onRedirect,
  title,
}: IMessageReturnToMessagesModalProps) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const redirectTimeoutId = window.setTimeout(
      onRedirect,
      IMESSAGE_RETURN_TO_MESSAGES_REDIRECT_DELAY_MS
    );

    return () => window.clearTimeout(redirectTimeoutId);
  }, [isOpen, onRedirect]);

  return (
    <StyledModalDialog
      isOpen={isOpen}
      handleClose={onClose}
      fluidWidth
      data-imessage-return-to-messages-modal
    >
      <StyledHeader slot={MDSDialogSlotName.Header}>
        <StyledTitle>{title}</StyledTitle>
      </StyledHeader>

      <StyledContent slot={MDSDialogSlotName.Content}>
        <span data-test-id="imessage-return-to-messages-spinner" aria-hidden="true">
          <MDSLoadingSpinner size={18} />
        </span>
        <StyledBody>Redirecting back to Messages...</StyledBody>
      </StyledContent>
    </StyledModalDialog>
  );
};

const StyledModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    box-sizing: border-box;
    width: min(440px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
    padding: ${({ theme }) => theme.spacing["7"]};
    gap: 0;
    outline: none;
  }

  .mds-dialog [slot="header"] {
    padding-block: 0;
  }

  .mds-dialog button[aria-label="Close"] {
    margin-top: ${({ theme }) => theme.spacing["1"]};
    margin-right: 0;
    border: 0;
  }

  [data-mds-name="dialog-body"] {
    overflow: visible;
    scrollbar-gutter: auto;
  }

  &[data-imessage-return-to-messages-modal],
  &[data-imessage-return-to-messages-modal] * {
    letter-spacing: 0;
  }
`;

const StyledHeader = styled.div`
  min-width: 0;
  padding: 0;
`;

const StyledTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  line-height: 36px;
`;

const StyledContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
`;

const StyledBody = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: 22px;
`;
